import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import dayjs from "dayjs";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { findByProfessores } from "../TDP/api-tdp";
import { findByUsuarioCodigo, findByCurrentUser, sendEmailPresencaAtradas } from "./api-acompanhamento";
import { ADMIN, COORDENADOR, PROFESSOR } from "../Usuario/Perfil";
import { dateFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import Loading from "../UI/Loading";

const BIMESTRES = [1, 2, 3, 4];

// Fundo sólido para a coluna fixa não deixar o conteúdo aparecer por trás ao rolar.
const headSx = { fontWeight: 600, bgcolor: "grey.100", whiteSpace: "nowrap" };
const groupStartSx = { borderLeft: 1, borderLeftColor: "divider" };
const stickySx = { position: "sticky", left: 0, zIndex: 1 };

const diasAtras = (data) => {
  const dias = dayjs().startOf("day").diff(dayjs(data).startOf("day"), "day");
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  return `há ${dias} dias`;
};

const DataCell = ({ data }) => (
  <TableCell>
    {data ? (
      <>
        <Typography variant="body2">{dateFormat(data)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {diasAtras(data)}
        </Typography>
      </>
    ) : (
      <Typography variant="body2" color="text.disabled">
        --
      </Typography>
    )}
  </TableCell>
);

const PresencaCell = ({ acompanhamento, bimestre }) => (
  <TableCell align="center" sx={bimestre === 1 ? groupStartSx : undefined}>
    <Typography variant="body2" fontWeight={500}>
      {acompanhamento[`quantAula${bimestre}`] ?? 0}/{acompanhamento[`quantPresenca${bimestre}`] ?? 0}
    </Typography>
    <Link
      component={RouterLink}
      to="/conferir-cronograma-presenca"
      state={{ tdp: acompanhamento.tdp, numeroBimestre: bimestre }}
      variant="caption"
    >
      Conferir
    </Link>
  </TableCell>
);

const EmptyState = ({ icon, children }) => (
  <Box
    sx={{
      py: 6,
      textAlign: "center",
      color: "text.secondary",
      border: 1,
      borderStyle: "dashed",
      borderColor: "divider",
      borderRadius: 2,
    }}
  >
    {icon}
    <Typography variant="body2" mt={1}>
      {children}
    </Typography>
  </Box>
);

export default function AcompanhamentoContainer() {
  const usuario = useSelector((state) => state.usuario);
  const isGestor = usuario?.perfil === ADMIN || usuario?.perfil === COORDENADOR;
  const isProfessor = usuario?.perfil === PROFESSOR;

  const [professores, setProfessores] = useState([]);
  const [professor, setProfessor] = useState(null);
  const [acompanhamentos, setAcompanhamentos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    if (!isGestor) return;
    findByProfessores().then((data) => {
      if (!data) return;
      if (data.error) {
        errorMessage(data.message);
      } else if (Array.isArray(data)) {
        setProfessores(data);
      }
    });
  }, [isGestor]);

  const codProfessor = professor?.id;

  useEffect(() => {
    // Gestores escolhem o professor; o professor vê o próprio acompanhamento.
    const request = isGestor
      ? codProfessor && findByUsuarioCodigo(codProfessor)
      : isProfessor && findByCurrentUser();
    if (!request) {
      setAcompanhamentos([]);
      return undefined;
    }

    let active = true;
    setIsLoading(true);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
          setAcompanhamentos([]);
        } else if (Array.isArray(data)) {
          setAcompanhamentos(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isGestor, isProfessor, codProfessor]);

  const enviarEmails = () => {
    setIsSendingEmail(true);
    sendEmailPresencaAtradas()
      .then((data) => {
        if (data?.error) {
          errorMessage(data.message);
          return;
        }
        success("E-mails de notificação estão sendo enviados.");
      })
      .finally(() => setIsSendingEmail(false));
  };

  const handleEnviarEmailPresencaAtrasadas = () => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja enviar os e-mails de notificação de presenças atrasadas?",
      buttons: [
        { label: "Sim", onClick: enviarEmails },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const renderTable = () => {
    if (isGestor && !professor) {
      return (
        <EmptyState icon={<PersonSearchOutlinedIcon fontSize="large" />}>
          Selecione um(a) professor(a) para ver o acompanhamento.
        </EmptyState>
      );
    }
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando acompanhamento..." />
        </Box>
      );
    }
    if (acompanhamentos.length === 0) {
      return <EmptyState>Nenhuma turma/disciplina encontrada.</EmptyState>;
    }

    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth: 960 }} aria-label="Acompanhamento de presenças e atividades">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ ...headSx, ...stickySx, zIndex: 2 }}>
                Turma - Disciplina
              </TableCell>
              <TableCell rowSpan={2} sx={headSx}>
                Últ. Presença
              </TableCell>
              <TableCell rowSpan={2} sx={headSx}>
                Últ. Atividade
              </TableCell>
              <TableCell colSpan={4} align="center" sx={{ ...headSx, ...groupStartSx }}>
                Presenças
                <Typography variant="caption" component="div" color="text.secondary">
                  Aulas / Aulas registradas
                </Typography>
              </TableCell>
              <TableCell colSpan={4} align="center" sx={{ ...headSx, ...groupStartSx }}>
                Atividades
              </TableCell>
            </TableRow>
            <TableRow>
              {[...BIMESTRES, ...BIMESTRES].map((bimestre, i) => (
                <TableCell
                  key={i}
                  align="center"
                  sx={{ ...headSx, ...(bimestre === 1 ? groupStartSx : {}) }}
                >
                  {bimestre}º Bim.
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {acompanhamentos.map((acompanhamento) => {
              const { tdp } = acompanhamento;
              return (
                <TableRow key={tdp.codigo ?? `${tdp.turma.descricao}-${tdp.disciplina.descricao}`} hover>
                  <TableCell sx={{ ...stickySx, bgcolor: "background.paper" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {tdp.turma.descricao}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tdp.disciplina.descricao}
                    </Typography>
                  </TableCell>
                  <DataCell data={acompanhamento.dataUltPresenca} />
                  <DataCell data={acompanhamento.dataUltAtividade} />
                  {BIMESTRES.map((bimestre) => (
                    <PresencaCell key={bimestre} acompanhamento={acompanhamento} bimestre={bimestre} />
                  ))}
                  {BIMESTRES.map((bimestre) => (
                    <TableCell key={bimestre} align="center" sx={bimestre === 1 ? groupStartSx : undefined}>
                      {acompanhamento[`quantAtividade${bimestre}`] ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Acompanhamento</Typography>
          <Typography variant="body2" color="text.secondary">
            Presenças e atividades registradas por turma e disciplina em cada bimestre.
          </Typography>
        </Box>
        {isGestor && (
          <Button
            variant="outlined"
            startIcon={<ForwardToInboxOutlinedIcon />}
            disabled={isSendingEmail}
            onClick={handleEnviarEmailPresencaAtrasadas}
          >
            Enviar E-mail Presenças Atrasadas
          </Button>
        )}
      </Stack>

      {isGestor && (
        <Autocomplete
          options={professores}
          value={professor}
          onChange={(event, value) => setProfessor(value)}
          getOptionLabel={(option) => option.nome ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="Nenhum professor encontrado"
          renderInput={(params) => <TextField {...params} size="small" label="Professor(a)" />}
          sx={{ maxWidth: 420, mb: 3 }}
        />
      )}

      {renderTable()}
    </Container>
  );
}
